import React from 'react';
import { Link } from 'react-router-dom';
import styles from './Trust.module.css';

export default function Trust() {
  return (
    <div className={styles.container}>
      <div className={styles.overlay} />
      <div className={styles.content}>
        <h1 className={styles.title}>Why You Can Trust Us</h1>
        <p className={styles.subtitle}>
          Your contact data is deeply personal. Here's why MyPeopleDatabase is the safest place
          to keep it — and how we've built trust into every layer of our platform.
        </p>

        <div className={styles.section}>
          <h2>Your Data Never Leaves Your Device</h2>
          <p>
            MyPeopleDatabase is a <strong>desktop application</strong> that stores all of your
            contact data locally on your own computer. Unlike cloud-first platforms that upload
            everything to remote servers, your information stays exactly where you put it — on
            your machine, under your control.
          </p>
          <ul className={styles.list}>
            <li>
              <strong>Local-first architecture:</strong> Your database lives on your hard drive,
              not on our servers. We never have access to your contacts.
            </li>
            <li>
              <strong>No cloud dependency:</strong> The app works fully offline. No internet
              connection is required to view, edit, or manage your people.
            </li>
            <li>
              <strong>No background syncing:</strong> We don't silently upload your data anywhere.
              What's on your device stays on your device.
            </li>
          </ul>
        </div>

        <div className={styles.section}>
          <h2>We Don't Monetize Your Relationships</h2>
          <p>
            Social networks and free contact apps have a hidden cost: they mine your relationships
            for advertising, recommendations, and data brokering.{' '}
            <strong>We will never do that.</strong>
          </p>
          <ul className={styles.list}>
            <li>
              <strong>No ads, ever:</strong> MyPeopleDatabase is a paid product — our revenue
              comes from you, not from selling your data to third parties.
            </li>
            <li>
              <strong>No data harvesting:</strong> We don't collect, analyze, or profile your
              contacts for any purpose.
            </li>
            <li>
              <strong>No "social graph" exploitation:</strong> Your connections are not used to
              fuel recommendation engines or suggest contacts to other users.
            </li>
          </ul>
        </div>

        <div className={styles.section}>
          <h2>The Four Pillars of Our Trust Model</h2>
          <div className={styles.pillarsGrid}>
            <div className={styles.pillar}>
              <div className={styles.pillarIcon}>🔒</div>
              <h3>Privacy by Architecture</h3>
              <p>
                We didn't bolt on privacy as an afterthought. The entire application is designed
                so that your data physically cannot reach us. There's no API call sending your
                contacts to our servers, because that code simply doesn't exist.
              </p>
            </div>
            <div className={styles.pillar}>
              <div className={styles.pillarIcon}>🗂️</div>
              <h3>Full Data Portability</h3>
              <p>
                You can export all of your data at any time in standard formats (CSV, VCF, XML).
                You're never locked in. If you decide to leave, every record goes with you — no
                questions asked.
              </p>
            </div>
            <div className={styles.pillar}>
              <div className={styles.pillarIcon}>🛡️</div>
              <h3>No Account Required</h3>
              <p>
                You can use MyPeopleDatabase without creating an account or providing any personal
                information beyond your purchase. The app works without sign-in, without
                telemetry, and without phoning home.
              </p>
            </div>
            <div className={styles.pillar}>
              <div className={styles.pillarIcon}>💰</div>
              <h3>Aligned Incentives</h3>
              <p>
                We make money when you buy the product — not when you use it. That means our
                incentive is to build the best tool possible, not to maximize engagement or
                extract data.
              </p>
            </div>
          </div>
        </div>

        <div className={styles.section}>
          <h2>How We Compare</h2>
          <p>
            Most contact management tools are built to serve the platform first and the user
            second. Here's how MyPeopleDatabase is different:
          </p>
          <ul className={styles.list}>
            <li>
              <strong>Google Contacts / Apple Contacts:</strong> Stored on corporate cloud servers
              and integrated into ecosystems that profile you for advertising. You don't control
              the infrastructure.
            </li>
            <li>
              <strong>CRM platforms (Salesforce, HubSpot):</strong> Designed for businesses, not
              people. Your personal relationships get treated as "leads" and "opportunities."
              Data lives on their servers.
            </li>
            <li>
              <strong>Social networks (LinkedIn, Facebook):</strong> Your connections are the
              product. They're analyzed, monetized, and used to target you and others with ads.
            </li>
            <li>
              <strong>MyPeopleDatabase:</strong> A personal tool that runs on your computer. No
              cloud, no ads, no tracking. Your relationships are yours alone.
            </li>
          </ul>
        </div>

        <div className={styles.section}>
          <h2>Our Transparency Commitment</h2>
          <p>
            Trust isn't just about technology — it's about honesty. Here's what we commit to:
          </p>
          <ul className={styles.list}>
            <li>
              <strong>Clear communication:</strong> If we ever change how data is handled, you'll
              know before it happens — not after.
            </li>
            <li>
              <strong>No dark patterns:</strong> We don't use manipulative UI tricks to get you to
              share more than you want to.
            </li>
            <li>
              <strong>Open roadmap:</strong> We build what our users ask for, and we're
              transparent about what's coming next.
            </li>
            <li>
              <strong>Responsive support:</strong> When you reach out, a real person responds.
              We're a small, dedicated team that cares about every user.
            </li>
          </ul>
        </div>

        <div className={styles.section}>
          <h2>Built for People Who Care About Privacy</h2>
          <p>
            We built MyPeopleDatabase because we were tired of handing over our most personal
            data — our relationships — to platforms that treat them as raw material for profit.
          </p>
          <p>
            Whether you're consolidating contacts scattered across Google, Apple, Outlook, and
            your phone, or you're looking for a secure way to maintain your personal network over
            a lifetime, MyPeopleDatabase gives you a place that's truly yours.
          </p>
          <p>
            <strong>Your people. Your data. Your device. No exceptions.</strong>
          </p>
        </div>

        <div className={styles.ctaSection}>
          <p>
            Ready to take ownership of your contact data? Read our data principles or try
            MyPeopleDatabase today.
          </p>
          <div className={styles.ctaLinks}>
            <Link to="/data-principles" className={`${styles.ctaLink} ${styles.ctaPrimary}`}>
              Our Data Principles
            </Link>
            <Link to="/download" className={`${styles.ctaLink} ${styles.ctaSecondary}`}>
              Download
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
